import React, { useRef, useState, useEffect, useCallback } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import "./QuantumTunnelingSimulator.css";

const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));

function useSimulationController({
  startX = -3.2,
  endX = 3.2,
  speed = 1.2,
}) {
  const stateRef = useRef({
    x: startX,
    vx: speed,
    running: false,
    passed: false,
    reflected: false,
    transmission: null,
  });

  const reset = useCallback(() => {
    stateRef.current.x = startX;
    stateRef.current.vx = Math.abs(speed);
    stateRef.current.running = false;
    stateRef.current.passed = false;
    stateRef.current.reflected = false;
    stateRef.current.transmission = null;
  }, [startX, speed]);

  return { stateRef, reset };
}

function Barrier({ width, height }) {
  const color = new THREE.Color(0x00ffff);
  return (
    <mesh position={[0, 0, 0]}>
      <boxGeometry args={[width, height, 1]} />
      <meshStandardMaterial
        color={color}
        opacity={0.4}
        transparent
        emissive={color}
        emissiveIntensity={0.7}
      />
    </mesh>
  );
}

function Particle({ stateRef, mode, energy, V0, L, startX, endX, speed, onUpdate }) {
  const meshRef = useRef();
  const r = 0.18;

  const transmission = (E, V0, L) => {
    if (E >= V0) return clamp(0.85 + (E - V0) * 0.03, 0.85, 1.0);
    const kappa = Math.sqrt(Math.max(0, V0 - E));
    return clamp(Math.exp(-2 * kappa * L), 0, 1);
  };

  useFrame((_, dt) => {
    const s = stateRef.current;
    if (!meshRef.current) return;

    if (s.running) {
      s.x += s.vx * dt;
      const left = -L / 2 - r;
      const right = L / 2 + r;

      if (mode === "classical") {
        if (!s.passed && s.vx > 0 && s.x >= left && s.x < right) {
          if (energy < V0) {
            s.vx = -Math.abs(s.vx);
            s.reflected = true;
          } else s.passed = true;
        }
        if (s.x < startX - 0.5 || s.x > endX + 0.5) s.running = false;
      } else {
        if (!s.passed && s.vx > 0 && s.x >= left) {
          s.transmission = transmission(energy, V0, L);
          s.passed = true;
        }
        if (s.x > endX + 0.5) s.running = false;
      }
    }

    meshRef.current.position.x = s.x;

    let opacity = 1;
    if (mode === "quantum" && s.transmission) {
      opacity = 0.2 + 0.8 * Math.sqrt(s.transmission);
    } else if (mode === "classical" && s.reflected) {
      opacity = 0.7;
    }

    const color = mode === "classical" ? new THREE.Color("#ffea00") : new THREE.Color("#00ffcc");
    meshRef.current.material.color = color;
    meshRef.current.material.emissive = color;
    meshRef.current.material.opacity = opacity;

    onUpdate &&
      onUpdate({
        x: s.x,
        running: s.running,
        passed: s.passed,
        transmission: s.transmission,
      });
  });

  return (
    <mesh ref={meshRef} position={[stateRef.current.x, 0, 0]}>
      <sphereGeometry args={[r, 32, 32]} />
      <meshStandardMaterial transparent emissiveIntensity={1.0} />
    </mesh>
  );
}

export default function QuantumTunnelingSimulator() {
  const [mode, setMode] = useState("classical");
  const [energy, setEnergy] = useState(2.0);
  const [V0, setV0] = useState(3.0);
  const [L, setL] = useState(0.6);
  const [speed, setSpeed] = useState(1.2);

  const startX = -3.2;
  const endX = 3.2;
  const { stateRef, reset } = useSimulationController({ startX, endX, speed });

  const [stats, setStats] = useState({
    x: startX,
    running: false,
    passed: false,
    transmission: null,
  });

  const handlePlay = () => {
    stateRef.current.vx = Math.abs(speed);
    stateRef.current.running = true;
  };
  const handlePause = () => (stateRef.current.running = false);
  const handleReset = () => reset();

  useEffect(() => {
    reset();
  }, [mode, energy, V0, L, speed, reset]);

  return (
    <div className="qt-root">

      <div className="qt-controls">
        <div className="row">
          <label>
            Mode
            <select value={mode} onChange={(e) => setMode(e.target.value)}>
              <option value="classical">Classical</option>
              <option value="quantum">Quantum</option>
            </select>
          </label>

          <label>
            Energy (E)
            <input
              type="range"
              min="0.1"
              max="10"
              step="0.1"
              value={energy}
              onChange={(e) => setEnergy(+e.target.value)}
            />
            <span className="small">{energy.toFixed(2)}</span>
          </label>

          <label>
            Barrier Height (V₀)
            <input
              type="range"
              min="0.1"
              max="10"
              step="0.1"
              value={V0}
              onChange={(e) => setV0(+e.target.value)}
            />
            <span className="small">{V0.toFixed(2)}</span>
          </label>

          <label>
            Barrier Width (L)
            <input
              type="range"
              min="0.2"
              max="3"
              step="0.1"
              value={L}
              onChange={(e) => setL(+e.target.value)}
            />
            <span className="small">{L.toFixed(2)}</span>
          </label>

          <label>
            Speed
            <input
              type="range"
              min="0.2"
              max="3"
              step="0.05"
              value={speed}
              onChange={(e) => setSpeed(+e.target.value)}
            />
            <span className="small">{speed.toFixed(2)}</span>
          </label>
        </div>

        <div className="row actions">
          <button onClick={handlePlay}>Play</button>
          <button onClick={handlePause}>Pause</button>
          <button onClick={handleReset}>Reset</button>
          <div className="stats">
            Pos: {stats.x.toFixed(2)} | Running: {stats.running ? "Yes" : "No"} | T: 
            {stats.transmission ? stats.transmission.toFixed(2) : "—"}
          </div>
        </div>
      </div>

      <div className="qt-scene">
        <Canvas camera={{ position: [0, 1.2, 5], fov: 50 }}>
          <ambientLight intensity={0.7} />
          <pointLight position={[4, 6, 5]} intensity={0.8} />
          <OrbitControls enableZoom={true} />
          <Barrier width={L} height={1.0 + V0 / 2} />
          <Particle
            stateRef={stateRef}
            mode={mode}
            energy={energy}
            V0={V0}
            L={L}
            startX={startX}
            endX={endX}
            speed={speed}
            onUpdate={setStats}
          />
        </Canvas>
      </div>

      <div className="qt-info">
        <p>
          <strong>Classical mode</strong> — particle bounces back if E &lt; V₀.  
          <strong>Quantum mode</strong> — wave can tunnel through with probability T ≈ e⁻²√(V₀−E)L.  
          Opacity represents wave amplitude.
        </p>
      </div>
    </div>
  );
}
